/**
 * Risk Streaming Event Parser
 *
 * Parses Server-Sent Events (SSE) from stage risk analysis streaming endpoint.
 * Handles event parsing, validation, and state aggregation for UI display.
 */

/**
 * Risk analysis phases configuration
 */
export const RISK_PHASES = {
  1: {
    id: 1,
    name: "Task Collection & Missing Task Detection",
    description: "Collecting tasks and detecting missing requirements",
  },
  2: {
    id: 2,
    name: "Evidence Gathering",
    description: "Extracting evidence from attachments and comments",
  },
  3: {
    id: 3,
    name: "Risk Analysis",
    description: "Analyzing risks for each task using AI",
  },
  4: {
    id: 4,
    name: "Error Detection",
    description: "Detecting errors and missing evidence",
  },
  5: {
    id: 5,
    name: "Risk Aggregation",
    description: "Calculating stage-level risk score",
  },
  6: {
    id: 6,
    name: "Recommendations",
    description: "Generating actionable recommendations",
  },
};

/**
 * Risk event type constants
 */
export const RISK_EVENT_TYPES = {
  // Phase 1: Task Collection
  TASK_COLLECTION_START: "task_collection_start",
  TASK_COLLECTION_PROGRESS: "task_collection_progress",
  TASK_COLLECTION_COMPLETE: "task_collection_complete",

  // Phase 1.5: Missing Task Detection
  MISSING_TASK_DETECTION_START: "missing_task_detection_start",
  MISSING_TASK_DETECTED: "missing_task_detected",
  INCOMPLETE_COVERAGE_DETECTED: "incomplete_coverage_detected",
  MISSING_TASK_DETECTION_COMPLETE: "missing_task_detection_complete",

  // Phase 2: Evidence Gathering
  EVIDENCE_GATHERING_START: "evidence_gathering_start",
  EVIDENCE_TASK_PROGRESS: "evidence_task_progress",
  EVIDENCE_ATTACHMENT_ANALYSIS: "evidence_attachment_analysis",
  EVIDENCE_COMMENT_ANALYSIS: "evidence_comment_analysis",
  EVIDENCE_GATHERING_COMPLETE: "evidence_gathering_complete",

  // Phase 3: Risk Analysis
  RISK_ANALYSIS_START: "risk_analysis_start",
  TASK_RISK_PROGRESS: "task_risk_progress",
  RISK_DETECTED: "risk_detected",
  RISK_ANALYSIS_COMPLETE: "risk_analysis_complete",

  // Phase 4: Error Detection
  ERROR_DETECTION_START: "error_detection_start",
  ERROR_DETECTED: "error_detected",
  MISSING_EVIDENCE_DETECTED: "missing_evidence_detected",
  ERROR_DETECTION_COMPLETE: "error_detection_complete",

  // Phase 5: Aggregation
  AGGREGATION_START: "aggregation_start",
  STAGE_RISK_CALCULATED: "stage_risk_calculated",
  AGGREGATION_COMPLETE: "aggregation_complete",

  // Phase 6: Recommendations
  RECOMMENDATIONS_START: "recommendations_start",
  RECOMMENDATION_GENERATED: "recommendation_generated",
  RECOMMENDATIONS_COMPLETE: "recommendations_complete",

  // System Events
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  ANALYSIS_COMPLETE: "analysis_complete",
};

/**
 * Phase status enum
 */
export const PHASE_STATUS = {
  PENDING: "pending",
  ACTIVE: "active",
  COMPLETE: "complete",
  ERROR: "error",
};

/**
 * Parse a single SSE event for risk analysis
 * @param {string} eventData - Raw event data from SSE
 * @returns {object|null} Parsed event object or null if invalid
 */
export function parseRiskEvent(eventData) {
  if (!eventData || !eventData.trim()) {
    return null;
  }

  try {
    // SSE format: "data: {json}\n\n"
    // Strip "data: " prefix if present
    const jsonString = eventData.replace(/^data:\s*/, "").trim();

    if (!jsonString) {
      return null;
    }

    const event = JSON.parse(jsonString);

    // Validate required fields
    if (!event.event_type || !event.phase) {
      console.warn("Invalid risk event: missing event_type or phase", event);
      return null;
    }

    return event;
  } catch (error) {
    console.error("Error parsing risk SSE event:", error, eventData);
    return null;
  }
}

/**
 * Initialize risk phase state structure
 * @returns {object} Initial phase state
 */
export function initializeRiskPhaseState() {
  const phaseState = {};

  Object.keys(RISK_PHASES).forEach((phaseId) => {
    phaseState[phaseId] = {
      status: PHASE_STATUS.PENDING,
      progress: 0,
      message: "",
      data: {},
      error: null,
      startTime: null,
      endTime: null,
      // Risk-specific fields
      tasksAnalyzed: 0,
      risksDetected: [],
      errorsDetected: [],
      missingEvidence: [],
      missingTasks: [],
      incompleteCoverage: [],
      coverageScore: null,
    };
  });

  return phaseState;
}

/**
 * Update risk phase state based on event
 * @param {object} phaseState - Current phase state
 * @param {object} event - Parsed event
 * @returns {object} Updated phase state
 */
export function updateRiskPhaseState(phaseState, event) {
  const { phase, event_type, progress, message, data, error, timestamp } = event;

  // Create a deep copy to avoid mutations
  const newPhaseState = JSON.parse(JSON.stringify(phaseState));

  // Get current phase
  const currentPhase = newPhaseState[phase];

  if (!currentPhase) {
    console.warn("Unknown risk phase:", phase);
    return phaseState;
  }

  // Update status based on event type
  if (event_type.includes("_start")) {
    currentPhase.status = PHASE_STATUS.ACTIVE;
    currentPhase.startTime = timestamp;
  } else if (event_type.includes("_complete")) {
    currentPhase.status = PHASE_STATUS.COMPLETE;
    currentPhase.progress = 100;
    currentPhase.endTime = timestamp;
  } else if (event_type === RISK_EVENT_TYPES.ERROR) {
    currentPhase.status = PHASE_STATUS.ERROR;
    currentPhase.error = error;
  }

  // Update progress
  if (progress !== undefined && progress !== null) {
    currentPhase.progress = Math.min(100, Math.max(0, progress));
  }

  // Update message
  if (message) {
    currentPhase.message = message;
  }

  // Merge data
  if (data && Object.keys(data).length > 0) {
    currentPhase.data = {
      ...currentPhase.data,
      ...data,
    };
  }

  // Handle specific event types
  switch (event_type) {
    case RISK_EVENT_TYPES.TASK_RISK_PROGRESS:
      if (data?.task_index !== undefined) {
        currentPhase.tasksAnalyzed = data.task_index + 1;
      }
      break;

    case RISK_EVENT_TYPES.RISK_DETECTED:
      if (data) {
        currentPhase.risksDetected.push({
          task_id: data.task_id,
          risk_level: data.risk_level,
          risk_type: data.risk_type,
          description: data.description,
          details: data.details,
          timestamp,
        });
      }
      break;

    case RISK_EVENT_TYPES.ERROR_DETECTED:
      if (data) {
        currentPhase.errorsDetected.push({
          task_id: data.task_id,
          error_type: data.error_type,
          description: data.description,
          details: data.details,
          timestamp,
        });
      }
      break;

    case RISK_EVENT_TYPES.MISSING_EVIDENCE_DETECTED:
      if (data) {
        currentPhase.missingEvidence.push({
          task_id: data.task_id,
          evidence_type: data.evidence_type,
          description: data.description,
          severity: data.severity,
          timestamp,
        });
      }
      break;

    case RISK_EVENT_TYPES.MISSING_TASK_DETECTED:
      if (data) {
        currentPhase.missingTasks.push({
          requirement: data.requirement,
          criticality: data.criticality,
          description: data.description,
          suggested_action: data.suggested_action,
          timestamp,
        });
      }
      break;

    case RISK_EVENT_TYPES.INCOMPLETE_COVERAGE_DETECTED:
      if (data) {
        currentPhase.incompleteCoverage.push({
          requirement: data.requirement,
          existing_task: data.existing_task,
          gap: data.gap,
          timestamp,
        });
      }
      break;

    case RISK_EVENT_TYPES.MISSING_TASK_DETECTION_COMPLETE:
      if (data) {
        currentPhase.coverageScore = data.coverage_score;
        currentPhase.data.missing_count = data.missing_count;
        currentPhase.data.incomplete_count = data.incomplete_count;
      }
      break;

    case RISK_EVENT_TYPES.RECOMMENDATION_GENERATED:
      if (data) {
        // Initialize recommendations array if it doesn't exist
        if (!currentPhase.recommendations) {
          currentPhase.recommendations = [];
        }
        // Add recommendation to array
        currentPhase.recommendations.push({
          priority: data.priority,
          category: data.category,
          action: data.action,
          details: data.details,
          task_id: data.task_id,
          timestamp,
        });
        // Also store in data for extractRecommendations() function
        currentPhase.data.recommendations = currentPhase.recommendations;
      }
      break;

    default:
      break;
  }

  return newPhaseState;
}

/**
 * Check if risk analysis is complete
 * @param {object} event - Parsed event
 * @returns {boolean} True if analysis complete
 */
export function isRiskAnalysisComplete(event) {
  return event.event_type === RISK_EVENT_TYPES.ANALYSIS_COMPLETE;
}

/**
 * Get current active phase in risk analysis
 * @param {object} phaseState - Phase state
 * @returns {number|null} Active phase ID or null
 */
export function getActiveRiskPhase(phaseState) {
  for (const [phaseId, phase] of Object.entries(phaseState)) {
    if (phase.status === PHASE_STATUS.ACTIVE) {
      return parseInt(phaseId);
    }
  }
  return null;
}

/**
 * Calculate overall risk analysis progress
 * @param {object} phaseState - Phase state
 * @returns {number} Progress percentage (0-100)
 */
export function calculateRiskAnalysisProgress(phaseState) {
  const phases = Object.values(phaseState);
  const totalProgress = phases.reduce((sum, phase) => sum + phase.progress, 0);
  return Math.round(totalProgress / phases.length);
}

/**
 * Extract stage-level risk metrics from final state
 * @param {object} phaseState - Phase state
 * @returns {object} Risk metrics
 */
export function extractStageRiskMetrics(phaseState) {
  const phase5 = phaseState[5]; // Aggregation phase

  if (!phase5 || !phase5.data) {
    return {
      stage_risk_score: null,
      stage_health: "unknown",
      critical_count: 0,
      high_risk_count: 0,
      error_count: 0,
    };
  }

  return {
    stage_risk_score: phase5.data.stage_risk_score || 0,
    stage_health: phase5.data.stage_health || "unknown",
    critical_count: phase5.data.critical_count || 0,
    high_risk_count: phase5.data.high_risk_count || 0,
    error_count: phase5.data.error_count || 0,
  };
}

/**
 * Extract all recommendations from phase 6
 * @param {object} phaseState - Phase state
 * @returns {array} Array of recommendations
 */
export function extractRecommendations(phaseState) {
  const phase6 = phaseState[6]; // Recommendations phase

  if (!phase6 || !phase6.data || !phase6.data.recommendations) {
    return [];
  }

  return phase6.data.recommendations || [];
}

/**
 * Get all detected risks across all phases
 * @param {object} phaseState - Phase state
 * @returns {array} Array of detected risks
 */
export function getAllDetectedRisks(phaseState) {
  const risks = [];

  Object.values(phaseState).forEach((phase) => {
    if (phase.risksDetected && phase.risksDetected.length > 0) {
      risks.push(...phase.risksDetected);
    }
  });

  return risks;
}

/**
 * Get all detected errors across all phases
 * @param {object} phaseState - Phase state
 * @returns {array} Array of detected errors
 */
export function getAllDetectedErrors(phaseState) {
  const errors = [];

  Object.values(phaseState).forEach((phase) => {
    if (phase.errorsDetected && phase.errorsDetected.length > 0) {
      errors.push(...phase.errorsDetected);
    }
  });

  return errors;
}

/**
 * Get all missing evidence items across all phases
 * @param {object} phaseState - Phase state
 * @returns {array} Array of missing evidence items
 */
export function getAllMissingEvidence(phaseState) {
  const missing = [];

  Object.values(phaseState).forEach((phase) => {
    if (phase.missingEvidence && phase.missingEvidence.length > 0) {
      missing.push(...phase.missingEvidence);
    }
  });

  return missing;
}

/**
 * Get all missing tasks across all phases
 * @param {object} phaseState - Phase state
 * @returns {array} Array of missing tasks
 */
export function getAllMissingTasks(phaseState) {
  const missing = [];

  Object.values(phaseState).forEach((phase) => {
    if (phase.missingTasks && phase.missingTasks.length > 0) {
      missing.push(...phase.missingTasks);
    }
  });

  return missing;
}

/**
 * Get all incomplete coverage items across all phases
 * @param {object} phaseState - Phase state
 * @returns {array} Array of incomplete coverage items
 */
export function getAllIncompleteCoverage(phaseState) {
  const incomplete = [];

  Object.values(phaseState).forEach((phase) => {
    if (phase.incompleteCoverage && phase.incompleteCoverage.length > 0) {
      incomplete.push(...phase.incompleteCoverage);
    }
  });

  return incomplete;
}

/**
 * Get coverage score from phase 1
 * @param {object} phaseState - Phase state
 * @returns {number|null} Coverage score or null
 */
export function getCoverageScore(phaseState) {
  const phase1 = phaseState[1];
  return phase1 && phase1.coverageScore !== null ? phase1.coverageScore : null;
}

/**
 * Format duration for display
 * @param {string} startTime - ISO timestamp
 * @param {string} endTime - ISO timestamp
 * @returns {string} Formatted duration
 */
export function formatDuration(startTime, endTime) {
  if (!startTime) {
    return "-";
  }

  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const durationMs = end - start;

  if (durationMs < 1000) {
    return `${durationMs}ms`;
  } else if (durationMs < 60000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Export risk analysis log as text
 * @param {object} phaseState - Phase state
 * @param {array} events - All events
 * @returns {string} Formatted log text
 */
export function exportRiskAnalysisLog(phaseState, _events) {
  let log = "Stage Risk Analysis Log\n";
  log += "=".repeat(80) + "\n";
  log += `Generated at: ${new Date().toISOString()}\n\n`;

  // Risk metrics summary
  const metrics = extractStageRiskMetrics(phaseState);
  log += "Stage Risk Metrics\n";
  log += "-".repeat(80) + "\n";
  log += `Stage Health: ${metrics.stage_health}\n`;
  log += `Risk Score: ${metrics.stage_risk_score}/100\n`;
  log += `Critical Risks: ${metrics.critical_count}\n`;
  log += `High Risks: ${metrics.high_risk_count}\n`;
  log += `Errors: ${metrics.error_count}\n\n`;

  // Phase summaries
  Object.entries(phaseState).forEach(([phaseId, phase]) => {
    const phaseInfo = RISK_PHASES[phaseId];
    log += `\nPhase ${phaseId}: ${phaseInfo.name}\n`;
    log += "-".repeat(80) + "\n";
    log += `Status: ${phase.status}\n`;
    log += `Progress: ${phase.progress}%\n`;
    log += `Duration: ${formatDuration(phase.startTime, phase.endTime)}\n`;

    if (phase.message) {
      log += `Message: ${phase.message}\n`;
    }

    if (phase.error) {
      log += `Error: ${phase.error}\n`;
    }

    log += "\n";
  });

  // Detected risks
  const risks = getAllDetectedRisks(phaseState);
  if (risks.length > 0) {
    log += "\n" + "=".repeat(80) + "\n";
    log += "Detected Risks\n";
    log += "=".repeat(80) + "\n\n";

    risks.forEach((risk, index) => {
      log += `[${index + 1}] Task: ${risk.task_id}\n`;
      log += `Risk Level: ${risk.risk_level}\n`;
      log += `Risk Type: ${risk.risk_type}\n`;
      log += `Description: ${risk.description}\n\n`;
    });
  }

  // Missing evidence
  const missing = getAllMissingEvidence(phaseState);
  if (missing.length > 0) {
    log += "\n" + "=".repeat(80) + "\n";
    log += "Missing Evidence\n";
    log += "=".repeat(80) + "\n\n";

    missing.forEach((item, index) => {
      log += `[${index + 1}] Task: ${item.task_id}\n`;
      log += `Evidence Type: ${item.evidence_type}\n`;
      log += `Description: ${item.description}\n`;
      log += `Severity: ${item.severity}\n\n`;
    });
  }

  // Recommendations
  const recommendations = extractRecommendations(phaseState);
  if (recommendations.length > 0) {
    log += "\n" + "=".repeat(80) + "\n";
    log += "Recommendations\n";
    log += "=".repeat(80) + "\n\n";

    recommendations.forEach((rec, index) => {
      log += `[${index + 1}] Priority: ${rec.priority}\n`;
      log += `Category: ${rec.category}\n`;
      log += `Action: ${rec.action}\n\n`;
    });
  }

  return log;
}

/**
 * Validate risk streaming request
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number
 * @returns {object} Validation result with { valid, errors }
 */
export function validateRiskStreamingRequest(workflowId, stageNumber) {
  const errors = [];

  if (!workflowId || typeof workflowId !== "string") {
    errors.push("Invalid workflow ID");
  }

  if (!stageNumber || typeof stageNumber !== "number" || stageNumber < 1) {
    errors.push("Invalid stage number");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

const riskStreamingEventParser = {
  RISK_PHASES,
  RISK_EVENT_TYPES,
  PHASE_STATUS,
  parseRiskEvent,
  initializeRiskPhaseState,
  updateRiskPhaseState,
  isRiskAnalysisComplete,
  getActiveRiskPhase,
  calculateRiskAnalysisProgress,
  extractStageRiskMetrics,
  extractRecommendations,
  getAllDetectedRisks,
  getAllDetectedErrors,
  getAllMissingEvidence,
  getAllMissingTasks,
  getAllIncompleteCoverage,
  getCoverageScore,
  formatDuration,
  exportRiskAnalysisLog,
  validateRiskStreamingRequest,
};

export default riskStreamingEventParser;
